/**
 * What the set actually made.
 *
 * The same beats in the same order as the storyboard, each holding its photos —
 * deliberately not a gallery. A gallery is sorted by arrival: it can tell you a
 * photo is bad, never which moment produced it or what that moment was missing.
 * Here the diagnosis sits in the beat's own header, so "no room bound" is
 * printed directly above the shots that each invented one.
 *
 * Every shot keeps a slot even before it has an image, so an unrun, still
 * rendering or failed shot is a visible gap rather than an absence.
 *
 * A set is a recipe you cook more than once and each run is a TAKE, so the runs
 * are a list you pick from. Without that they interleave newest-first and
 * neither take reads as a set of anything.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, mediaUrl, type DayBeat, type SetRun } from '../lib/api';
import { money } from '../lib/compose';

function Img({ src, alt }: { src: string | null | undefined; alt: string }) {
  const [url, setUrl] = useState<string | null>(src?.startsWith('http') ? src : null);
  useEffect(() => {
    let live = true;
    if (!src) { setUrl(null); return; }
    if (src.startsWith('http')) { setUrl(src); return; }
    void mediaUrl(src).then((u) => { if (live) setUrl(u); }).catch(() => undefined);
    return () => { live = false; };
  }, [src]);
  if (!url) return <span className="ph" aria-label={alt} />;
  return <img src={url} alt={alt} />;
}

export interface SetPhotosProps {
  setId: string;
  onChanged: () => void | Promise<void>;
}

export default function SetPhotos({ setId, onChanged }: SetPhotosProps) {
  const [beats, setBeats] = useState<DayBeat[]>([]);
  const [runs, setRuns] = useState<SetRun[]>([]);
  const [run, setRun] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get<{ beats: DayBeat[]; runs: SetRun[]; run: string | null }>(
        `/sets/${setId}/renders${run ? `?run=${run}` : ''}`);
      setBeats(r.beats);
      setRuns(r.runs);
      if (!run && r.run) setRun(r.run);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load these photos');
    }
  }, [setId, run]);

  useEffect(() => { void load(); }, [load]);

  // Anything mid-flight is the only thing on this screen that changes by itself.
  const working = beats.some((b) =>
    b.renders.some((r) => r.status === 'queued' || r.status === 'running'));
  useEffect(() => {
    if (!working) return;
    const t = setInterval(() => void load(), 4000);
    return () => clearInterval(t);
  }, [working, load]);

  async function rerun(beatId: string) {
    setBusy(true);
    try {
      await api.post(`/sets/${setId}/run`, { beatIds: [beatId] });
      await onChanged();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start that beat');
    } finally { setBusy(false); }
  }

  if (runs.length === 0) {
    return (
      <div className="card">
        <div className="notice">
          <b>This set has not been run yet.</b> Run it and the photos land here, under the beat that
          made each one.
        </div>
      </div>
    );
  }

  const current = runs.find((r) => r.batchId === run);
  const total = runs.reduce((n, r) => n + r.count, 0);

  return (
    <div className="card">
      {error && <div className="notice bad" style={{ marginBottom: 14 }}>{error}</div>}

      <div className="runbar">
        <select
          className="field" style={{ width: 'auto' }}
          value={run} onChange={(e) => setRun(e.target.value)}
        >
          {runs.map((r, i) => (
            <option key={r.batchId} value={r.batchId}>
              {i === 0 ? 'Latest run' : 'Run'} · {new Date(r.startedAt).toLocaleString()}
            </option>
          ))}
          <option value="all">All runs together</option>
        </select>
        <span className="mono faint">
          {run === 'all'
            ? `${total} photos · ${runs.length} runs`
            : current
              ? `${current.count} ${current.count === 1 ? 'photo' : 'photos'} · ${money(current.costUsd)}`
                + (current.failed ? ` · ${current.failed} failed` : '')
              : ''}
        </span>
      </div>

      <div className="daybeats">
        {beats.map((b) => {
          const slots = b.shots ?? [];
          // A shot deleted from the storyboard leaves photos behind; they are
          // still hers, so they are shown rather than silently dropped.
          const orphans = b.renders.filter((r) => !r.shotId || !slots.some((s) => s.id === r.shotId));
          return (
            <div key={b.beatId} className="daybeat">
              <div className="dhead">
                <span className="when mono">{b.timeLabel || '—'}</span>
                <span className="nm">{b.name || 'Untitled beat'}</span>
                <span className={`where${b.placeName ? '' : ' miss'}`}>
                  {b.placeName ?? 'no room bound — each shot invents one'}
                </span>
                <span className="right">
                  <span className="pill mono">{b.renders.length} of {b.shotCount}</span>
                  <button className="btn btn-sm" disabled={busy} onClick={() => void rerun(b.beatId)}>
                    {b.renders.length ? 'Re-roll beat' : 'Run this beat'}
                  </button>
                </span>
              </div>

              <div className="dstrip">
                {slots.map((slot, i) => {
                  const mine = b.renders.filter((r) => r.shotId === slot.id);
                  const shown = mine[0];
                  return (
                    <div key={slot.id} className="dslot">
                      <div className="fr">
                        {shown?.src && shown.status === 'done'
                          ? <Img src={shown.src} alt={`Shot ${i + 1}`} />
                          : (
                            <span className={`ph${shown?.status === 'failed' ? ' bad' : ''}`}>
                              {shown?.status === 'running' || shown?.status === 'queued'
                                ? 'rendering'
                                : shown?.status === 'failed' ? 'failed' : 'not run'}
                            </span>
                          )}
                        {mine.length > 1 && <span className="takes mono">{mine.length} takes</span>}
                      </div>
                      <div className="dcap mono">
                        <span>shot {i + 1}</span>
                        <span className={`tier t-${slot.tier}`}>{slot.tier}</span>
                        {slot.camera === 'no-face' && <span className="faint">no-face</span>}
                      </div>
                    </div>
                  );
                })}

                {orphans.map((r) => (
                  <div key={r.id} className="dslot">
                    <div className="fr">
                      {r.src ? <Img src={r.src} alt="Photo" /> : <span className="ph">gone</span>}
                    </div>
                    <div className="dcap mono"><span className="faint">shot since removed</span></div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
